// /admin/playbooks* Wrapper — alle Endpoints Owner-only (siehe api/src/routes/admin.ts).
// Deckt ab: Playbook-CRUD + PDF-Attachment-Upload (multipart) für die Antwort-Grundlage.

import { apiFetch, getApiBase } from './api';

export interface AdminPlaybook {
  id: string;
  title: string;
  triggers: string[];
  answer: string;
  active: boolean;
  attachmentKey: string | null;
  attachmentName: string | null;
}

export interface PlaybookInput {
  title: string;
  triggers: string[];
  answer: string;
  active: boolean;
  attachmentKey?: string | null;
  attachmentName?: string | null;
}

export function fetchPlaybooks(accessToken: string): Promise<AdminPlaybook[]> {
  return apiFetch<AdminPlaybook[]>('/admin/playbooks', { accessToken });
}

export function createPlaybook(
  accessToken: string,
  input: PlaybookInput,
): Promise<{ id: string }> {
  return apiFetch<{ id: string }>('/admin/playbooks', {
    method: 'POST',
    accessToken,
    json: input,
  });
}

export function updatePlaybook(
  accessToken: string,
  id: string,
  input: Partial<PlaybookInput>,
): Promise<{ ok: boolean }> {
  return apiFetch<{ ok: boolean }>(`/admin/playbooks/${id}`, {
    method: 'PUT',
    accessToken,
    json: input,
  });
}

export function deletePlaybook(accessToken: string, id: string): Promise<{ ok: boolean }> {
  return apiFetch<{ ok: boolean }>(`/admin/playbooks/${id}`, {
    method: 'DELETE',
    accessToken,
  });
}

// Lädt eine PDF-Datei hoch (multipart), gibt {key,name} zurück.
// apiFetch setzt JSON-Header — für multipart nutzen wir fetch direkt, damit der
// Browser die Boundary selbst setzt (wie settings-api.ts uploadAvatar).
export async function uploadPlaybookPdf(
  accessToken: string,
  file: File,
): Promise<{ key: string; name: string }> {
  const fd = new FormData();
  fd.append('file', file);
  const res = await fetch(`${getApiBase()}/admin/playbooks/attachment`, {
    method: 'POST',
    headers: { Authorization: `Bearer ${accessToken}` },
    body: fd,
  });
  if (!res.ok) {
    const body = (await res.json().catch(() => ({}))) as { error?: string };
    throw new Error(body.error ?? `Upload fehlgeschlagen (HTTP ${res.status})`);
  }
  return (await res.json()) as { key: string; name: string };
}

// Lädt das archivierte PDF (Owner-only, Bearer nötig) als Blob und öffnet es in
// einem neuen Tab. Ein plain <a href> kann keinen Auth-Header mitsenden, daher
// dieser Fetch-→-Blob-URL-Umweg.
export async function openPlaybookPdf(
  accessToken: string,
  attachmentId: string,
): Promise<void> {
  const res = await fetch(`${getApiBase()}/admin/playbooks/attachment/${attachmentId}`, {
    headers: { Authorization: `Bearer ${accessToken}` },
  });
  if (!res.ok) {
    throw new Error(`Download fehlgeschlagen (HTTP ${res.status})`);
  }
  const blob = await res.blob();
  const url = URL.createObjectURL(blob);
  window.open(url, '_blank', 'noopener,noreferrer');
  // Blob-URL nach kurzer Frist freigeben (der Tab hat sie dann geladen).
  window.setTimeout(() => URL.revokeObjectURL(url), 60_000);
}

// Extrahiert die uuid aus einem Attachment-Key `playbooks/<uuid>.pdf`.
export function attachmentIdFromKey(key: string): string {
  return key.replace(/^playbooks\//, '').replace(/\.pdf$/i, '');
}
